import styled from '@emotion/styled/macro';
import { BaseButton, Variables } from '../../../base';
import { CdCard, CdPanel, CdSectionCard, CdSectionTitle } from './timeline-detail-card.styles';
import { ICodingFieldChange } from './coding-detail.types';

export { CdCard, CdPanel, CdSectionCard, CdSectionTitle };

const monoFont = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

export const CdHeadlineCard = styled(CdCard)`
    padding: 18px 22px;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
`;

export const CdIconWrap = styled.div`
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: ${Variables.ThemeLight.PrimaryMild1};
    border: 1px solid ${Variables.ThemeLight.PrimaryMild2};
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
`;

export const CdHeadlineMain = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
    flex: 1;
`;

export const CdHeadlineTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

export const CdHeadlineTitle = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 18px;
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: -0.01em;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const CdHeadlineMeta = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const CdHeadlineSummary = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const CdHeadlineAside = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
    flex: none;
`;

export const CdLabel = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 9px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextXMild};
`;

export const CdMonoValue = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
    font-variant-numeric: tabular-nums;
    word-break: break-all;
    text-align: right;
`;

export const CdRulesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const CdRuleCard = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 12px 14px;
    background: ${Variables.ThemeLight.Background1};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 8px;
`;

export const CdRuleBadge = styled.span`
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 54px;
    height: 24px;
    border-radius: 6px;
    background: ${Variables.ThemeLight.AttentionMild1};
    border: 1px solid ${Variables.ThemeLight.AttentionMild2};
    font-family: ${Variables.Fonts.calibre};
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.AttentionConstant6};
`;

export const CdRuleText = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextStrong};
    text-wrap: pretty;
`;

export const CdChangesHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    flex-wrap: wrap;
    gap: 8px;
`;

export const CdToggleButton = styled(BaseButton, { shouldForwardProp: prop => prop !== '$active' })<{ $active?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border: 1px solid
        ${({ $active }) => ($active ? Variables.ThemeLight.PrimaryMild2 : Variables.ThemeLight.Border2)};
    border-radius: 6px;
    background: ${({ $active }) =>
        $active ? Variables.ThemeLight.PrimaryMild1 : Variables.ThemeLight.Background2};
    color: ${({ $active }) =>
        $active ? Variables.ThemeLight.PrimaryConstant6 : Variables.ThemeLight.TextMedium};
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
`;

const changeTone = (op: ICodingFieldChange['op']) => {
    switch (op) {
        case 'added':
            return {
                bg: Variables.ThemeLight.SuccessMild1,
                border: Variables.ThemeLight.SuccessMild2,
                fg: Variables.ThemeLight.SuccessConstant6,
            };
        case 'changed':
            return {
                bg: Variables.ThemeLight.PrimaryMild1,
                border: Variables.ThemeLight.PrimaryMild2,
                fg: Variables.ThemeLight.PrimaryConstant6,
            };
        case 'removed':
        default:
            return {
                bg: Variables.ThemeLight.WarningMild1,
                border: Variables.ThemeLight.WarningMild2,
                fg: Variables.ThemeLight.WarningConstant6,
            };
    }
};

export const CdChangeCard = styled.div<{ $op: ICodingFieldChange['op'] }>`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 14px;
    background: ${({ $op }) => changeTone($op).bg};
    border: 1px solid ${({ $op }) => changeTone($op).border};
    border-radius: 8px;
    flex-wrap: wrap;
`;

export const CdChangeOp = styled.span<{ $op: ICodingFieldChange['op'] }>`
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${({ $op }) => changeTone($op).fg};
`;

export const CdChangeBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

export const CdChangeLabel = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const CdChangeField = styled.span`
    font-family: ${monoFont};
    font-size: 12px;
    line-height: 1.3;
    color: ${Variables.ThemeLight.TextMild};
    word-break: break-all;
`;

export const CdChangeValue = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextStrong};
    word-break: break-all;
    text-align: right;
`;

export const CdChangesList = styled.div<{ $scrollable?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    ${({ $scrollable }) =>
        $scrollable
            ? `
        max-height: 320px;
        overflow-y: auto;
        padding-right: 4px;
    `
            : ''}
`;

export const CdDiffEmptyPayload = styled.div`
    padding: 14px 16px;
`;

export const CdDiffEmptyPayloadText = styled.p`
    margin: 0 0 10px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMild};
`;

export const CdDiffEmptyPayloadCode = styled.pre`
    margin: 0;
    font-family: ${monoFont};
    font-size: 12px;
    line-height: 1.5;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const CdDiffWrap = styled.div`
    margin-top: 16px;
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 8px;
    overflow: hidden;
`;

export const CdDiffHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    background: ${Variables.ThemeLight.Background1};
    border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    flex-wrap: wrap;
`;

export const CdDiffTitle = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const CdDiffSubtitle = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMild};
`;

export const CdDiffLegend = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const CdDiffLegendSwatch = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: ${Variables.ThemeLight.SuccessMild1};
    border: 1px solid ${Variables.ThemeLight.SuccessMild2};
`;

export const CdDiffScroll = styled.div`
    max-height: 360px;
    overflow: auto;
    background: ${Variables.ThemeLight.Background3};
`;

export const CdDiffNav = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    flex-wrap: wrap;
`;

export const CdDiffNavButton = styled(BaseButton)`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 6px;
    background: ${Variables.ThemeLight.Background3};
    color: ${Variables.ThemeLight.TextMedium};
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    line-height: 1;
    cursor: pointer;

    &:disabled {
        color: ${Variables.ThemeLight.TextXMild};
        cursor: default;
        opacity: 0.55;
    }

    &:hover:not(:disabled) {
        background: ${Variables.ThemeLight.Background1};
        color: ${Variables.ThemeLight.TextStrong};
    }
`;

export const CdDiffNavCount = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
    white-space: nowrap;
`;

export const CdDiffRegionAnchor = styled.div`
    scroll-margin-top: 8px;
`;

export const CdDiffBody = styled.div`
    min-width: max-content;
    padding: 8px 0;
`;

export const CdDiffEllipsis = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 22px;
    padding-left: 62px;
    color: ${Variables.ThemeLight.TextXMild};
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-style: italic;
`;

export const CdDiffEllipsisDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${Variables.ThemeLight.TextXMild};
`;

export const CdDiffLine = styled.div<{ $added?: boolean }>`
    display: flex;
    height: 22px;
    line-height: 22px;
    background: ${({ $added }) => ($added ? Variables.ThemeLight.SuccessMild1 : 'transparent')};
`;

export const CdDiffMarker = styled.span<{ $added?: boolean }>`
    flex: none;
    width: 16px;
    text-align: center;
    color: ${({ $added }) => ($added ? Variables.ThemeLight.SuccessConstant6 : 'transparent')};
    font-family: ${monoFont};
    font-size: 12px;
    user-select: none;
`;

export const CdDiffLineNum = styled.span`
    flex: none;
    width: 40px;
    text-align: right;
    padding-right: 12px;
    user-select: none;
    color: ${Variables.ThemeLight.TextXMild};
    font-family: ${monoFont};
    font-size: 12px;
`;

export const CdDiffCode = styled.code<{ $added?: boolean }>`
    font-family: ${monoFont};
    font-size: 12px;
    white-space: pre;
    padding-right: 20px;
    font-weight: ${({ $added }) => ($added ? 600 : 400)};
`;

export const CdJsonKey = styled.span`
    color: ${Variables.ThemeLight.TextMild};
`;

export const CdJsonColon = styled.span`
    color: ${Variables.ThemeLight.TextXMild};
`;

export const CdJsonString = styled.span`
    color: ${Variables.ThemeLight.SuccessConstant6};
`;

export const CdJsonPrimitive = styled.span`
    color: ${Variables.ThemeLight.PrimaryConstant6};
`;

export const CdJsonPlain = styled.span`
    color: ${Variables.ThemeLight.TextMedium};
`;

export const CdEmptyState = styled.p`
    margin: 0;
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMild};
    font-style: italic;
`;
